'use client';

import { ColumnFiltersState, createColumnHelper, getFilteredRowModel } from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  ReactElement, Suspense, useMemo,useState
} from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import InvitationsTable from '@/app/admin/components/invitationsTable';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { useFlag } from '@/components/flags/provider';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { Heading } from '@/components/typography/heading';
import { showDeleteUser } from '@/flags';
import {
  useBlockUserMutation,
  useDeleteUserMutation,
  useInviteUserMutation,
  useOrgInvitationsQuery,
  useOrgUsersQuery,
  useSetUserRoleMutation,
  useUnblockUserMutation
} from '@/hooks/queries/useOrg';
import { ROLE_TO_FGA, SELECTABLE_FGA_RELATIONS, SelectableFgaRelation } from '@/types/fga';
import { User } from '@/types/user';

import styles from './page.module.scss';


export const dynamic = 'force-dynamic';

type UsersListProps = {
  users: User[];
  columnFilters: ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  showDeleteUserFlag: boolean;
  onRoleChange: ( user: User, role: SelectableFgaRelation ) => void;
  onDisable: ( user: User ) => void;
  onEnable: ( user: User ) => void;
  onDelete: ( user: User ) => void;
};

function UsersList( {
  users,
  columnFilters,
  setColumnFilters,
  showDeleteUserFlag,
  onRoleChange,
  onDisable,
  onEnable,
  onDelete
}: UsersListProps ): ReactElement {
  const columnHelper = createColumnHelper<User>();

  const columns = useMemo( () => [
    columnHelper.accessor( 'email', {
      id: 'email',
      header: 'Email',
      cell: info => info.getValue(),
      size: showDeleteUserFlag ? 200 : 250,
      enableSorting: true,
      enableColumnFilter: true
    } ),
    columnHelper.accessor( 'name', {
      id: 'name',
      header: 'Name',
      cell: info => info.getValue() || '-',
      size: showDeleteUserFlag ? 150 : 200,
      enableSorting: true,
      enableColumnFilter: true
    } ),
    columnHelper.accessor( 'id', {
      id: 'id',
      header: 'User ID',
      cell: info => info.getValue(),
      size: showDeleteUserFlag ? 200 : 250,
      enableSorting: true
    } ),
    columnHelper.accessor( 'roles', {
      id: 'role',
      header: 'Role',
      cell: info => {
        const user = info.row.original;
        const current: SelectableFgaRelation = user.roles.some( r => r.name in ROLE_TO_FGA ) ? 'admin' : 'developer';
        return (
          <Menu
            key={ user.id + '-role' }
            className={ styles.role_select }
            data-testid={ `user-role-${ user.id }` }
            variant='select'
            value={ current }
          >
            { SELECTABLE_FGA_RELATIONS.map( r => (
              <MenuItem
                key={ r }
                value={ r }
                onClick={ () => {
                  if ( r !== current ) onRoleChange( user, r );
                } }
              />
            ) ) }
          </Menu>
        );
      },
      size: 150,
      enableSorting: false
    } ),
    columnHelper.accessor( 'blocked', {
      id: 'blocked',
      header: 'Status',
      cell: info => ( info.getValue() ? 'Disabled' : 'Active' ),
      size: showDeleteUserFlag ? 100 : 150,
      enableSorting: true
    } ),
    ...( showDeleteUserFlag
      ? [
          columnHelper.display( {
            id: 'actions',
            header: 'Actions',
            cell: ( { row } ) => {
              const user = row.original;
              const actionIcon = <FiMoreVertical key={ user.id + '-icon' } />;
              return (
                <Menu
                  icon={ actionIcon }
                  key={ user.id + '-menu' }
                  data-testid={ `user-actions-menu-${ user.id }` }
                  variant='menu'
                >
                  {
                    !user.blocked && (
                      <MenuItem value="disable" onClick={ () => onDisable( user ) } />
                    )
                  }
                  {
                    user.blocked && (
                      <MenuItem value="enable" onClick={ () => onEnable( user ) } />
                    )
                  }
                  <MenuItem value="delete" onClick={ () => onDelete( user ) } />
                </Menu>
              );
            },
            size: 100,
            meta: {
              testId: 'actions-column-header'
            }
          } )
        ]
      : [] )
  ], [ showDeleteUserFlag, onRoleChange, onDisable, onEnable, onDelete ] );

  const sortedUsers = useMemo( () => {
    return [ ...users ].sort( ( a, b ) => a.email.localeCompare( b.email ) );
  }, [ users ] );

  const table = useNativeTable( {
    data: sortedUsers,
    columns,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters
    },
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 20
      }
    }
  } );

  const emailFilter = columnFilters.find( f => f.id === 'email' )?.value as string || '';
  const nameFilter = columnFilters.find( f => f.id === 'name' )?.value as string || '';

  return (
    <>
      <div className={ clsx( 'layout__row', 'pad', 'space_none' ) }>
        <Input
          id='email-filter'
          label='Filter by Email'
          type='text'
          placeholder='Search emails...'
          value={ emailFilter }
          onChange={ e => {
            const value = e.target.value;
            setColumnFilters( prev => {
              if( value ) {
                return [ ...prev.filter( f => f.id !== 'email' ), { id: 'email', value } ];
              }
              return prev.filter( f => f.id !== 'email' );
            } );
          } }
        />
        <Input
          id='name-filter'
          label='Filter by Name'
          type='text'
          placeholder='Search names...'
          value={ nameFilter }
          onChange={ e => {
            const value = e.target.value;
            setColumnFilters( prev => {
              if( value ) {
                return [ ...prev.filter( f => f.id !== 'name' ), { id: 'name', value } ];
              }
              return prev.filter( f => f.id !== 'name' );
            } );
          } }
        />
      </div>
      <NativeTable table={ table } />
    </>
  );
}

function AdminPage() {
  const [ inviteEmail, setInviteEmail ] = useState<string>( '' );
  const [ showDeleteModal, setShowDeleteModal ] = useState<boolean>( false );
  const [ showInviteModal, setShowInviteModal ] = useState<boolean>( false );
  const [ userToDelete, setUserToDelete ] = useState<User | null>( null );
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>( [] );

  const showDeleteUserFlag = useFlag( showDeleteUser );

  const { data: invitations = [], isLoading: isLoadingInvitations } = useOrgInvitationsQuery();
  const { data: users = [], isLoading: isLoadingUsers } = useOrgUsersQuery();

  const blockUserMutation = useBlockUserMutation();
  const deleteUserMutation = useDeleteUserMutation();
  const inviteUserMutation = useInviteUserMutation();
  const setUserRoleMutation = useSetUserRoleMutation();
  const unblockUserMutation = useUnblockUserMutation();

  const isLoading = isLoadingInvitations || isLoadingUsers;

  const handleInviteSubmit = async( e: React.FormEvent ) => {
    e.preventDefault();

    if( !inviteEmail ) {
      toast.error( 'Email is required' );
      return;
    }

    inviteUserMutation.mutate(
      {
        email: inviteEmail
      },
      {
        onSuccess: () => {
          toast.success( `Invitation sent to ${ inviteEmail }` );
          setShowInviteModal( false );
          setInviteEmail( '' );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to send invitation' );
        }
      }
    );
  };

  const handleCloseModal = () => {
    setShowInviteModal( false );
    setInviteEmail( '' );
  };

  const handleRoleChange = ( user: User, role: SelectableFgaRelation ) => {
    setUserRoleMutation.mutate(
      { userId: user.id, role },
      {
        onSuccess: () => {
          toast.success( `Role updated for ${ user.email }` );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to update role' );
        }
      }
    );
  };

  const handleDeleteClick = ( user: User ) => {
    setUserToDelete( user );
    setShowDeleteModal( true );
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal( false );
    setUserToDelete( null );
  };

  const handleDeleteConfirm = () => {
    if ( !userToDelete ) return;

    deleteUserMutation.mutate(
      {
        userId: userToDelete.id
      },
      {
        onSuccess: () => {
          toast.success( `User ${ userToDelete.email } deleted successfully` );
          setShowDeleteModal( false );
          setUserToDelete( null );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to delete user' );
        }
      }
    );
  };

  const handleDisableUser = ( user: User ) => {
    blockUserMutation.mutate(
      {
        userId: user.id
      },
      {
        onSuccess: () => {
          toast.success( `User ${ user.email } disabled successfully` );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to disable user' );
        }
      }
    );
  };

  const handleEnableUser = ( user: User ) => {
    unblockUserMutation.mutate(
      {
        userId: user.id
      },
      {
        onSuccess: () => {
          toast.success( `User ${ user.email } enabled successfully` );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to enable user' );
        }
      }
    );
  };

  return (
    <div className='page'>
      <ToastContainer />
      <div className={ clsx( 'layout__row', 'pad', 'space_between' ) }>
        <Heading>Organization Users ({users.length})</Heading>
        <Button
          variant='primary'
          data-testid='invite-user-button'
          onClick={ () => setShowInviteModal( true ) }
        >
          Invite User
        </Button>
      </div>
      {
        isLoading
          ? <Loader />
          : (
              <UsersList
                users={ users }
                columnFilters={ columnFilters }
                setColumnFilters={ setColumnFilters }
                showDeleteUserFlag={ showDeleteUserFlag }
                onRoleChange={ handleRoleChange }
                onDisable={ handleDisableUser }
                onEnable={ handleEnableUser }
                onDelete={ handleDeleteClick }
              />
            )
      }
      <div className={ clsx( 'layout__row', 'pad', 'space_between' ) } style={ { marginTop: '2rem' } }>
        <Heading as="h2">Pending Invitations ({invitations.length})</Heading>
      </div>
      {
        isLoading
          ? <Loader />
          : (
              <InvitationsTable
                invitations={ invitations }
              />
            )
      }
      {
        showInviteModal && (
          <Modal>
            <form data-testid='invite-modal' onSubmit={ handleInviteSubmit }>
              <Heading as="h2" size="md">Invite User to Organization</Heading>
              <div className={ clsx( 'layout__col', 'pad' ) }>
                <label className='auth_flow_label' htmlFor='email'>
                  Email *
                </label>
                <input
                  className='auth_flow_input'
                  id='email'
                  name='email'
                  onChange={ e => setInviteEmail( e.target.value ) }
                  placeholder='user@example.com'
                  required
                  type='email'
                  value={ inviteEmail }
                />
              </div>
              <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
                <Button
                  variant='secondary'
                  size='sm'
                  data-testid='cancel-invite-button'
                  disabled={ inviteUserMutation.isPending }
                  onClick={ handleCloseModal }
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  data-testid='send-invitation-button'
                  disabled={ inviteUserMutation.isPending }
                  type='submit'
                >
                  { inviteUserMutation.isPending ? 'Sending...' : 'Send Invitation' }
                </Button>
              </div>
            </form>
          </Modal>
        )
      }
      {
        showDeleteModal && userToDelete && (
          <Modal>
            <div className={ clsx( 'layout__col', 'pad', styles.delete_modal_content ) } data-testid='delete-user-modal'>
              <Heading as="h2" size="md">Delete User</Heading>
              <p>Are you sure you want to delete this user from your organization?</p>
              <p className={ styles.user_email }>
                { userToDelete.email }
              </p>
              <div className={ clsx( 'layout__row', 'pad', 'flex_right', styles.button_container ) }>
                <Button
                  variant='secondary'
                  size='sm'
                  data-testid='cancel-delete-button'
                  disabled={ deleteUserMutation.isPending }
                  onClick={ handleDeleteCancel }
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  className={ styles.delete_button }
                  data-testid='confirm-delete-user-button'
                  disabled={ deleteUserMutation.isPending }
                  onClick={ handleDeleteConfirm }
                >
                  { deleteUserMutation.isPending ? 'Deleting...' : 'Delete User' }
                </Button>
              </div>
            </div>
          </Modal>
        )
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <AdminPage />
    </Suspense>
  );
}